import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { RemoteContent, Title, useDocumentMeta, useDocuments } from '@jupi/ui'
import { DocumentRow } from './documents/DocumentRow'
import {
  EMPTY_VIEW,
  PAGE_SIZE,
  filterFromView,
  hasFilters,
  paramsFromView,
  viewFromParams,
  withFilter,
  type DocumentView,
} from './documents/documentFilters'

// The club's documents: rules, forms, bulletins, meeting papers. What a member
// sees is decided by the API; the board's papers are simply not in the answer
// for anyone else. Uploading and editing are in the admin app.
export default function DocumentsPage() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const [params, setParams] = useSearchParams()
  const view = useMemo(() => viewFromParams(params), [params])
  const show = (next: DocumentView, replace = false) => setParams(paramsFromView(next), { replace })

  // The search box is local so typing is instant; the URL (and so the query)
  // follows it a moment after the last keystroke.
  const [text, setText] = useState(view.q)
  useEffect(() => setText(view.q), [view.q])
  useEffect(() => {
    if (text === view.q) return
    const timer = setTimeout(() => show(withFilter(view, { q: text }), true), 300)
    return () => clearTimeout(timer)
  }, [text, view])

  const meta = useDocumentMeta()
  const { data, isLoading, error } = useDocuments(filterFromView(view))
  const documents = data?.documents ?? []
  const pages = data ? Math.ceil(data.total / (data.pageSize || PAGE_SIZE)) : 0
  const categories = meta.data?.categories ?? []
  const categoryNames = useMemo(() => {
    const lang = language.slice(0, 2)
    return new Map(
      categories.map((c) => [c.categoryKey, (lang === 'sv' ? c.nameSv : lang === 'en' ? c.nameEn : null) || c.nameFi]),
    )
  }, [categories, language])

  return (
    <Box>
      <Title label={t('documents.title')} />
      <Stack spacing={2}>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('documents.search')}
          slotProps={{
            htmlInput: { 'aria-label': t('documents.search'), maxLength: 100 },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mdi:magnify" width={20} />
                </InputAdornment>
              ),
            },
          }}
          fullWidth
          size="small"
        />

        {categories.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }} role="group" aria-label={t('documents.title')}>
            <Chip
              label={t('documents.allCategories')}
              color={view.category === '' ? 'primary' : 'default'}
              variant={view.category === '' ? 'filled' : 'outlined'}
              onClick={() => show(withFilter(view, { category: '' }))}
            />
            {categories.map((c) => (
              <Chip
                key={c.categoryKey}
                label={`${categoryNames.get(c.categoryKey)} (${c.documentCount})`}
                color={view.category === c.categoryKey ? 'primary' : 'default'}
                variant={view.category === c.categoryKey ? 'filled' : 'outlined'}
                onClick={() => show(withFilter(view, { category: c.categoryKey }))}
              />
            ))}
          </Box>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
          <TextField
            select
            size="small"
            label={t('documents.year')}
            value={view.year ?? ''}
            onChange={(e) => show(withFilter(view, { year: e.target.value === '' ? null : Number(e.target.value) }))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('documents.allYears')}</MenuItem>
            {(meta.data?.years ?? []).map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            options={(meta.data?.tags ?? []).map((tag) => tag.tag)}
            value={view.tag || null}
            onChange={(_, tag) => show(withFilter(view, { tag: tag ?? '' }))}
            renderInput={(inputProps) => <TextField {...inputProps} label={t('documents.tag')} />}
            sx={{ minWidth: 220, flex: { sm: 1 }, maxWidth: { sm: 360 } }}
          />
          {hasFilters(view) ? (
            <Button
              onClick={() => show(EMPTY_VIEW)}
              startIcon={<Icon icon="mdi:filter-off-outline" width={18} />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            >
              {t('documents.clear')}
            </Button>
          ) : null}
        </Stack>

        <RemoteContent isLoading={isLoading} error={error}>
          {data ? (
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {t('documents.count', { count: data.total })}
            </Typography>
          ) : null}
          {documents.length === 0 && !isLoading ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {hasFilters(view) ? t('documents.noMatch') : t('documents.none')}
            </Typography>
          ) : (
            <Box>
              {documents.map((document) => (
                <DocumentRow
                  key={document.documentId}
                  document={document}
                  categoryName={categoryNames.get(document.categoryKey) ?? document.categoryKey}
                  language={language}
                  onTag={(tag) => show(withFilter(view, { tag }))}
                />
              ))}
            </Box>
          )}
          {pages > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Pagination
                count={pages}
                page={view.page}
                onChange={(_, page) => {
                  show({ ...view, page })
                  window.scrollTo({ top: 0 })
                }}
                getItemAriaLabel={(type, page, selected) =>
                  type === 'page' ? `${t('documents.page', { page })}${selected ? ' ✓' : ''}` : type
                }
              />
            </Box>
          ) : null}
        </RemoteContent>
      </Stack>
    </Box>
  )
}
